import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { RevenueObservationService } from './../../revenue/shared/revenue-observation.service';
import { InvoiceModificationComponent } from './../invoice-modification/invoice-modification.component';
import { ReceiptService } from './../../revenue/shared/receipt/receipt.service';
import { InvoiceBuilderComponent } from './../invoice-builder/invoice-builder.component';
import { CreditCardRefundRequest } from './../../revenue/shared/credit-card/credit-card-refund-request.model';
import { CreditCardPaymentInfo } from './../../revenue/shared/credit-card/credit-card-payment-info.model';
import { CreditCardPaymentResult } from './../../revenue/shared/credit-card/credit-card-payment-result.model';
import { CreditCardService } from './../../revenue/shared/credit-card/credit-card.service';
import { InvoiceUpsertResultDto, InvoicePaymentHeaderUpsertResultDto } from './../dto/invoice-upsert-result.dto';
import { InvoiceUpsertDto, initializedInvoiceUpsertDto } from '../dto/invoice-upsert.dto';
import { InvoiceLineItemUpsertDto } from '../dto/invoice-line-item-upsert.dto';
import { InvoicePaymentUpsertDto } from '../dto/invoice-payment-upsert.dto';
import { AdvancedPaymentInfoPaymentHeaderMapDto } from '../../revenue/shared/invoice-payment/dto/advanced-payment-info-payment-header-map.dto';
import { AdvancedCreditCardPaymentResult } from '../../revenue/shared/credit-card/advanced-credit-card-payment-result.model';
import { NavigationService } from './../../shared/navigation/navigation.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NotificationService } from './../../shared/notification/notification.service';
import { TranslationService } from './../../shared/translation/translation.service';
import { PaymentHeaderDto, initializedPaymentHeaderDto } from './../dto/payment-header.dto';
import { CreditCardGateway } from './../../revenue/shared/credit-card-gateway/credit-card-gateway.model';
import { CreditCardGatewayService } from './../../revenue/shared/credit-card-gateway/credit-card-gateway.service';
import { InvoiceService } from './../../revenue/shared/invoice/invoice.service';
import { ActivatedRoute, Router, Navigation } from '@angular/router';
import { ProfileService } from './../../profiles/shared/profile.service';
import { ProfileRead } from './../../profiles/shared/profile-read.model';
import { getSafe } from 'src/app/shared/util/get-safe';
import { initializedInvoiceAdjustmentUpsertDto } from '../dto/invoice-adjustment-upsert.dto';
import { UserService } from 'src/app/users/shared/user.service';
import { Title } from '@angular/platform-browser';
import { formatDate } from '@angular/common';

enum InvoiceImbalance {
  None,
  OutstandingLineItemWithoutAnAdjustment,
  PaymentAppliedWithoutAnAdjustment,
}

@Component({
  selector: 'wl-invoice-edit',
  templateUrl: './invoice-edit.component.html',
  styleUrls: ['./invoice-edit.component.less'],
  providers: [CreditCardService],
    standalone: false
})
export class InvoiceEditComponent implements OnInit {
  @ViewChild(InvoiceBuilderComponent) invoiceBuilder: InvoiceBuilderComponent;
  @ViewChild(InvoiceModificationComponent) invoiceModification: InvoiceModificationComponent;
  @ViewChild(ConfirmDialog) confirmDialog: ConfirmDialog;

  public invoice: InvoiceUpsertDto;
  public isLoading = true;
  public isSaving = false;
  public profile: ProfileRead;
  public profileId: number;
  public pledgeId: number = 0;
  public invoiceNum: number;
  public showRefundFailedAlert = false;
  public showModificationPrompt = false;
  public showNoPaymentAdjustmentAlert = false;
  public showNoLineItemAdjustmentAlert = false;
  public showChangeProfile = false;
  public modificationItemName = '';
  public saveAccess = false;
  private shouldRefund = false;
  public addingNewPayment = false;
  public paymentTypeInvalid: boolean = false;

  public creditCardPayment: CreditCardPaymentInfo;
  public previousNavigation: Navigation;

  constructor(
    private _route: ActivatedRoute,
    private _profileService: ProfileService,
    private _invoiceService: InvoiceService,
    private _creditCardGatewayService: CreditCardGatewayService,
    private _notificationService: NotificationService,
    private _router: Router,
    private _translationService: TranslationService,
    private _navigationService: NavigationService,
    private _creditCardService: CreditCardService,
    private _receiptService: ReceiptService,
    private _revenueObservationService: RevenueObservationService,
    private _confirmationService: ConfirmationService,
    private _userService: UserService,
    private _title: Title
  ) {
    this.profile = new ProfileRead();
    this.profileId = 0;
    this.invoice = initializedInvoiceUpsertDto();
    this.creditCardPayment = new CreditCardPaymentInfo();

    const currentNavigation = this._router.getCurrentNavigation();
    if (currentNavigation) {
      this.previousNavigation = currentNavigation.previousNavigation;
    }
  }

  ngOnInit() {
    this._creditCardGatewayService.getCreditCardGateways().then((data: Array<CreditCardGateway>) => {
      if (data.length > 0) {
        this.creditCardPayment.CCGatewayID = data[0].CCGatewayID;
      }
    });
    this._route.params.subscribe((id) => {
      if (+id['id']) {
        this.invoiceNum = +id['id'];
        this.loadExistingInvoice();
      }
    });
    this._route.queryParams.subscribe((params) => {
      if (!this.invoiceNum || !params.id) {
        if (params['profileId']) {
          const today = new Date(formatDate(new Date(), 'yyyy-MM-dd 12:00', 'en-US'));
          this.invoice = initializedInvoiceUpsertDto();
          this.creditCardPayment = new CreditCardPaymentInfo();
          this.profileId = parseInt(params['profileId'], 10);
          this.invoice.InvoiceHeader.ProfileId = this.profileId;
          this.invoice.InvoiceHeader.InvoiceDate = today;
          this.invoice.InvoiceHeader.DateDue = today;
          this.invoice.PaymentHeader = initializedPaymentHeaderDto();
          this.invoice.PaymentHeader.ProfileID = this.profileId;
          this.invoice.LineItemAdjustmentHeader = initializedInvoiceAdjustmentUpsertDto();
          this.invoice.PaymentAdjustmentHeader = initializedInvoiceAdjustmentUpsertDto();
          this._profileService.getProfile(this.profileId).then((data: ProfileRead) => {
            this.profile = data;
            this.isLoading = false;
          });
        }
        if (params['pledgeId']) {
          this.pledgeId = parseInt(params['pledgeId'], 10);
        }
      }
    });
    this._revenueObservationService.observeNewPayments().subscribe(() => {
      const modificationPotential = this.invoice.LineItems.filter((x) => x.RelatedRevenueItems.length > 0);
      if (modificationPotential.length > 0) {
        this.modificationItemName = modificationPotential[0].Item;
        this.showModificationPrompt = true;
      }
    });
    this._userService.getCurrentUserSecurityElementAccessLevel('Revenue_Module').then((level) => {
      this.saveAccess = level > 1;
    });
  }

  viewOrPrint() {
    if (this.invoiceNum) {
      this._navigationService.navigateToInvoiceReport(this.invoiceNum);
    }
  }

  public invoiceModified() {
    this.loadExistingInvoice(true);
  }

  public saveClick() {
    if (!this.validateRequiredFields()) {
      return;
    }
    if (this.isLineItemAdjustmentTotalMismatch()) {
      this._notificationService.translateError('invoices.invoice-builder.line-item-adjustment-mismatch');
      return;
    } else if (this.isPaymentAdjustmentTotalMismatch()) {
      this._notificationService.translateError('invoices.invoice-builder.payment-adjustment-mismatch');
      return;
    }
    const imbalanceResult = this.checkForImbalance();
    if (imbalanceResult === InvoiceImbalance.PaymentAppliedWithoutAnAdjustment) {
      this.showNoPaymentAdjustmentAlert = true;
    } else if (imbalanceResult === InvoiceImbalance.OutstandingLineItemWithoutAnAdjustment) {
      this.showNoLineItemAdjustmentAlert = true;
    } else if (this.invoice.PaymentHeader.Amount > 0 && this.hasNegativeLineItems()) {
      this._notificationService.translateError('invoices.invoice-builder.negative-line-item-error');
    } else if (this.hasNegativeLineItemPaymentToApply()) {
      this._notificationService.translateError('invoices.invoice-builder.negative-line-item-payment');
    } else if (this.isPaymentOverpaid()) {
      this._notificationService.translateError('invoices.invoice-builder.payment-overpaid');
    } else if (this.isThereALineItemOverpaid()) {
      this._notificationService.translateError('invoices.invoice-builder.line-item-overpaid');
    } else {
      this.saveInvoice();
    }
  }

  private isPaymentOverpaid(): boolean {
    const totalPaymentAmountToApply = this.invoice.LineItems.reduce((acc, li) => acc + (Number(li.PaymentAmountToApply) || 0), 0);
    const totalAdjustedAmountDue = this.invoice.LineItems.reduce((acc, li) => acc + (Number(li.AdjustedAmountDue) || 0), 0);

    return totalPaymentAmountToApply > 0 && totalPaymentAmountToApply > totalAdjustedAmountDue;
  }

  private isThereALineItemOverpaid(): boolean {
    return this.invoice.LineItems.some((li) => li.PaymentAmountToApply > 0 && li.PaymentAmountToApply > li.AdjustedAmountDue);
  }

  private isLineItemAdjustmentTotalMismatch(): boolean {
    let lineItemAdjustmentToApplyTotal = this.invoice.LineItems.reduce((acc, item) => acc + (item.AdjustmentAmountToApply ?? 0), 0);
    return lineItemAdjustmentToApplyTotal != this.invoice.LineItemAdjustmentHeader.AdjustmentAmount;
  }

  private isPaymentAdjustmentTotalMismatch(): boolean {
    let paymentAdjustmentToApplyTotal = this.invoice.LineItems.reduce(
      (acc, item) => acc + (item.Payments ?? []).reduce((payAcc, payment) => payAcc + (payment.AdjustmentAmountToApply ?? 0), 0),
      0
    );
    return paymentAdjustmentToApplyTotal != this.invoice.PaymentAdjustmentHeader.AdjustmentAmount;
  }

  private validateRequiredFields(): boolean {
    // PaymentHeader only appears when adding a new payment
    if (!this.addingNewPayment) {
      return true;
    }

    this.paymentTypeInvalid = false;

    const header = this.invoice?.PaymentHeader;

    // If _paymentType is present, ensure PaymentType is selected
    if (!header || header.PaymentTypeID === 0) {
      this.paymentTypeInvalid = true;
      this._notificationService.translateError('invoices.invoice-builder.payment-tab.payment-type-required-message');
      return false;
    }

    return true;
  }

  public async saveInvoice() {
    // Only tokenize when staff have actually added a new CC payment row.
    // paymentHeaderComponent is always instantiated ([hidden] not *ngIf), so checking its existence
    // alone is not sufficient — we must also confirm addingNewPayment is true and the selected
    // payment type is a credit card type before invoking the Preczn tokenizer.
    const isNewCcPayment =
      this.invoiceBuilder?.paymentsComponent?.addingNewPayment &&
      getSafe(() => this.invoice.PaymentHeader._paymentType.CreditCardType);

    if (isNewCcPayment && this.invoiceBuilder?.paymentsComponent?.paymentHeaderComponent) {
      this.creditCardPayment.Amount = this.invoice.PaymentHeader.Amount;
      try {
        await this.invoiceBuilder.paymentsComponent.paymentHeaderComponent.prepareForSubmit();
      } catch (e) {
        this._notificationService.error(e?.message ?? this._translationService.getInstant('revenue.credit-card-input.tokenize-error'));
        return;
      }
    }
    this.isSaving = true;
    this._revenueObservationService.requestInvoiceUpsert();

    // Populated only on the single-AP CC path; used below to keep that path's refund-on-upsert-failure
    // behavior byte-for-byte identical to before the multi-AP branch was introduced.
    let singleApCcResult: CreditCardPaymentResult | null = null;
    // Populated only on the multi-AP CC path with one synthetic entry per gateway-charge that failed
    // *before* upsertInvoice was ever called. These are merged into result.PaymentHeaderResults so the
    // existing warn-toast handling below (unchanged) surfaces them the same way it surfaces backend-side
    // per-AP persistence failures.
    let multiApFailedHeaderResults: InvoicePaymentHeaderUpsertResultDto[] = [];

    this.processCreditPayment()
      .then((data: CreditCardPaymentResult | AdvancedCreditCardPaymentResult[] | null) => {
        if (Array.isArray(data)) {
          const succeededResults = data.filter((r) => r.TransactionResult.Succeeded);
          const failedResults = data.filter((r) => !r.TransactionResult.Succeeded);

          if (succeededResults.length === 0) {
            failedResults.forEach((r) => this._notificationService.error(r.TransactionResult.RawTransactionResult));
            this.isSaving = false;
            return;
          }

          // AP1 success is kept even if AP2's gateway charge failed - no rollback of AP1's payment.
          this.applyMultiApCreditCardResultsToInvoice(succeededResults);
          multiApFailedHeaderResults = this.buildFailedAdvancedPaymentHeaderResults(failedResults);
        } else if (data && !data.Succeeded) {
          this._notificationService.error(data.RawTransactionResult);
          this.isSaving = false;
          return;
        } else if (data) {
          singleApCcResult = data;
          this.invoice.PaymentHeader.CC_CreditCardNum = data.CcId;
        }

        return this.confirmRefund().then(
          (shouldRefund: boolean) => {
            if (this.pledgeId > 0) {
              this.invoice.LineItems.forEach((li) => (li.PledgeId = this.pledgeId));
            }
            return this._invoiceService
              .upsertInvoice(this.invoice)
              .then((result: InvoiceUpsertResultDto) => {
                if (multiApFailedHeaderResults.length > 0) {
                  result.PaymentHeaderResults = [...(result.PaymentHeaderResults ?? []), ...multiApFailedHeaderResults];
                }
                if (result.UpsertSuccessful) {
                  if (result.InvoiceNum === this.invoice.InvoiceHeader.InvoiceNum) {
                    this._notificationService.success(
                      this._translationService.getInstant('invoices.invoice-builder.invoice-updated') + result.InvoiceNum.toString()
                    );
                    this.loadExistingInvoice(true);
                  } else {
                    this._notificationService.success(
                      this._translationService.getInstant('invoices.invoice-builder.invoice-added') + result.InvoiceNum.toString()
                    );
                  }

                  const failedApResults = (result.PaymentHeaderResults ?? []).filter((r) => !r.Success);

                  failedApResults.forEach((r) => {
                    const detail = r.Messages?.length ? r.Messages.join(' ') : 'An error occurred processing this payment.';
                    this._notificationService.warn(`Payment #${r.Index + 1} could not be applied: ${detail}`);
                  });

                  if (failedApResults.length === 0) {
                    // Full success — follow normal navigation including previousNavigation
                    if (this.previousNavigation) {
                      this._router.navigateByUrl(this.previousNavigation.finalUrl);
                    } else {
                      this._navigationService.navigateToInvoiceEdit(result.InvoiceNum);
                    }
                  } else {
                    // Partial success — keep staff on the invoice so they can see the warn toasts
                    // and correct the failed AP payment. For new invoices, navigate to the
                    // assigned InvoiceNum but do not follow previousNavigation.
                    if (result.InvoiceNum !== this.invoice.InvoiceHeader.InvoiceNum) {
                      this._navigationService.navigateToInvoiceEdit(result.InvoiceNum);
                    }
                    // For existing invoices, loadExistingInvoice(true) already ran above — stay on page.
                  }
                  if (shouldRefund) {
                    this.refundPayments();
                  }
                  this.invoice.PaymentHeader = initializedPaymentHeaderDto();
                  this.invoice.PaymentHeaders = undefined;
                  this.invoice.LineItemAdjustmentHeader = initializedInvoiceAdjustmentUpsertDto();
                  this.invoice.PaymentAdjustmentHeader = initializedInvoiceAdjustmentUpsertDto();

                  this.invoiceBuilder.cancelNewPaymentsAndAdjustments();

                  if (this.invoiceBuilder.sendAutomaticReceipt) {
                    this._receiptService.SendReceipt(result.InvoiceNum);
                  }
                } else {
                  if (result.Messages && result.Messages.length > 0) {
                    if (singleApCcResult && singleApCcResult.Succeeded) {
                      const refund = new CreditCardRefundRequest();
                      refund.CreditCardTransactionId = +singleApCcResult.CcId.replace('ccid_', '');
                      refund.CreateAdjustments = false;
                      refund.RefundAmount = this.invoice.PaymentHeader.Amount;
                      this._creditCardService.refundCreditCardPayment(refund);
                    }
                    result.Messages.forEach((x) => {
                      this._notificationService.error(x);
                    });
                  }
                }

                this.isSaving = false;
              })
              .catch((error) => {
                this._notificationService.httpError(error);
                this.isSaving = false;
              });
          },
          () => {
            this.isSaving = false;
            return;
          }
        );
      })
      .catch((error) => {
        this._notificationService.httpError(error);
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  private refundPayments() {
    const refundPromises = [];
    for (const payment of this.invoice.LineItems.filter(
      (li) => !li.DeleteLineItem && li.Payments.some((p) => !p.DeletePayment && p.AdjustmentAmountToApply < 0 && !!p.CC_CreditCardNum)
    )
      .flatMap((x) => x.Payments)
      .filter((p) => !p.DeletePayment && p.AdjustmentAmountToApply < 0 && !!p.CC_CreditCardNum)) {
      const refund = new CreditCardRefundRequest();
      refund.CreditCardTransactionId = +payment.CC_CreditCardNum.replace('ccid_', '');
      refund.CreateAdjustments = false;
      refund.RefundAmount = -payment.AdjustmentAmountToApply;
      refundPromises.push(this._creditCardService.refundCreditCardPayment(refund));
    }
    if (refundPromises.length > 0) {
      // wait for all refund requests to complete and let the end use know if something went wrong
      Promise.all(refundPromises).then((refundData) => {
        if (refundData.some((x) => !x.Succeeded)) {
          this.showRefundFailedAlert = true;
        } else {
          this._notificationService.success(this._translationService.getInstant('invoices.invoice-builder.refund-successful'));
        }
      });
    }
  }

  public cancelClick() {
    if (this.previousNavigation) {
      this._router.navigateByUrl(this.previousNavigation.finalUrl);
    } else {
      this._navigationService.navigateToProfileView(this.profileId);
    }
  }

  private loadExistingInvoice(ignoreCache?: boolean) {
    this._invoiceService.getInvoiceUpsertDto(this.invoiceNum, ignoreCache).then((data: InvoiceUpsertDto) => {
      this.invoice = data;
      this.invoice.PaymentHeader = initializedPaymentHeaderDto();
      this.invoice.PaymentHeader.ProfileID = this.invoice.InvoiceHeader.ProfileId;
      this.invoice.LineItemAdjustmentHeader = initializedInvoiceAdjustmentUpsertDto();
      this.invoice.PaymentAdjustmentHeader = initializedInvoiceAdjustmentUpsertDto();
      this.profileId = this.invoice.InvoiceHeader.ProfileId;
      this._profileService.getProfile(this.profileId).then((profile: ProfileRead) => {
        this.profile = profile;
        this.isLoading = false;
      });
      if (this.invoice.InvoiceHeader.InvoiceNum) {
        this._title.setTitle(
          this._translationService.getInstantTitlecase('common.revenue.invoice') + ' ' + this.invoice.InvoiceHeader.InvoiceNum
        );
      }
      this._revenueObservationService.invoiceLoaded();
    });
  }

  private hasNegativeLineItems(): boolean {
    return this.invoice.LineItems.some((li) => li.AdjustedAmountDue < 0);
  }

  private hasNegativeLineItemPaymentToApply(): boolean {
    return this.invoice.LineItems.some((li) => li.PaymentAmountToApply < 0);
  }

  private processCreditPayment(): Promise<CreditCardPaymentResult | AdvancedCreditCardPaymentResult[] | null> {
    if (!getSafe(() => this.invoice.PaymentHeader._paymentType.CreditCardType)) {
      return Promise.resolve(null);
    }

    if (this.isMultiApPayment()) {
      const maps = this.buildAdvancedCreditCardPaymentMaps();
      return this._creditCardService.processAdvancedCreditCardPayments(maps);
    }

    // Single-AP path - unchanged from before the multi-AP branch was introduced.
    this.creditCardPayment.Amount = this.invoice.PaymentHeader.Amount;
    this.creditCardPayment.ProfileId = this.invoice.PaymentHeader.ProfileID;
    this.creditCardPayment.InvoiceNumber = this.invoice.InvoiceHeader.InvoiceNum;
    this._creditCardService.fillPaymentHeaderFieldsWithCcInfo(this.creditCardPayment, this.invoice.PaymentHeader);
    return this._creditCardService.processCreditCardPayment(this.creditCardPayment);
  }

  /**
   * True when the invoice's line items span more than one accounting package for this payment - i.e. at least
   * one non-deleted line item's assigned payment type belongs to a different accounting package than the
   * payment header's payment type. In that case a single CC gateway charge cannot service the whole payment;
   * it must be split per accounting package (and, within that, per gateway) via the Advanced endpoint.
   */
  private isMultiApPayment(): boolean {
    const headerAccountingPackageId = getSafe(() => this.invoice.PaymentHeader._paymentType.AccountingPackageId);
    return this.invoice.LineItems.some(
      (li) => !li.DeleteLineItem && li._paymentType && li._paymentType.AccountingPackageId !== headerAccountingPackageId
    );
  }

  /**
   * Builds one AdvancedPaymentInfoPaymentHeaderMapDto per CC gateway represented among the line items being
   * paid, each containing one PaymentHeaderDto per distinct payment type within that gateway. Mirrors
   * MultiInvoicePaymentComponent.processAdvancedCreditPayments().
   */
  private buildAdvancedCreditCardPaymentMaps(): AdvancedPaymentInfoPaymentHeaderMapDto[] {
    const payableLineItems = this.invoice.LineItems.filter(
      (li) => !li.DeleteLineItem && li.PaymentAmountToApply > 0 && !!li._paymentType
    );

    const byGateway = new Map<number, InvoiceLineItemUpsertDto[]>();
    for (const lineItem of payableLineItems) {
      const gatewayId = lineItem._paymentType.CcGatewayId;
      if (!byGateway.has(gatewayId)) {
        byGateway.set(gatewayId, []);
      }
      byGateway.get(gatewayId)?.push(lineItem);
    }

    const maps: AdvancedPaymentInfoPaymentHeaderMapDto[] = [];

    for (const [gatewayId, gatewayLineItems] of byGateway) {
      const groupAmount = gatewayLineItems.reduce((sum, li) => sum + li.PaymentAmountToApply, 0);

      const ccInfo: CreditCardPaymentInfo = { ...this.creditCardPayment };
      ccInfo.CCGatewayID = gatewayId;
      ccInfo.Amount = groupAmount;
      ccInfo.ProfileId = this.invoice.PaymentHeader.ProfileID;

      const byPaymentType = new Map<number, InvoiceLineItemUpsertDto[]>();
      for (const lineItem of gatewayLineItems) {
        const typeId = lineItem._paymentType.PaymentTypeId;
        if (!byPaymentType.has(typeId)) {
          byPaymentType.set(typeId, []);
        }
        byPaymentType.get(typeId)?.push(lineItem);
      }

      const paymentHeaders: PaymentHeaderDto[] = [];
      for (const [, typeLineItems] of byPaymentType) {
        const paymentType = typeLineItems[0]._paymentType;
        const header: PaymentHeaderDto = {
          ...this.invoice.PaymentHeader,
          Amount: typeLineItems.reduce((sum, li) => sum + li.PaymentAmountToApply, 0),
          _paymentType: paymentType,
          PaymentType: paymentType.PaymentType,
          PaymentTypeID: paymentType.PaymentTypeId,
        };
        this._creditCardService.fillPaymentHeaderFieldsWithCcInfo(ccInfo, header);
        paymentHeaders.push(header);
      }

      maps.push({ PaymentHeaders: paymentHeaders, CreditCardPaymentInfo: ccInfo });
    }

    return maps;
  }

  /**
   * Applies the succeeded advanced CC charge results to the invoice ahead of upsertInvoice():
   * - Populates invoice.PaymentHeaders (plural) with one header per succeeded gateway/payment-type group,
   *   stamped with the CC transaction id returned for that group's gateway charge.
   * - Nulls out invoice.PaymentHeader (singular) since the backend rejects both being populated at once
   *   (enforced in InvoiceUpsertIntegrator).
   * - Manually builds the InvoicePaymentUpsertDto entries on each paid line item, since
   *   PrepareInvoicePaymentUpsertDtos on the backend only auto-builds lineItem.Payments when
   *   invoice.PaymentHeader (singular) is populated.
   */
  private applyMultiApCreditCardResultsToInvoice(succeededResults: AdvancedCreditCardPaymentResult[]) {
    const payerProfileId = this.invoice.PaymentHeader.ProfileID;
    const invoiceNum = this.invoice.InvoiceHeader.InvoiceNum;

    const succeededHeaders: PaymentHeaderDto[] = [];
    for (const r of succeededResults) {
      for (const header of r.PaymentInfoPaymentHeaderMap.PaymentHeaders) {
        succeededHeaders.push({ ...header, CC_CreditCardNum: r.TransactionResult.CcId });
      }
    }

    this.invoice.PaymentHeaders = succeededHeaders;
    this.invoice.PaymentHeader = null;

    const succeededPaymentTypeIds = new Set(succeededHeaders.map((h) => h.PaymentTypeID));

    this.invoice.LineItems.filter(
      (li) =>
        !li.DeleteLineItem && li.PaymentAmountToApply > 0 && li._paymentType && succeededPaymentTypeIds.has(li._paymentType.PaymentTypeId)
    ).forEach((li) => {
      if (!li.Payments) {
        li.Payments = [];
      }
      const header = succeededHeaders.find((h) => h.PaymentTypeID === li._paymentType.PaymentTypeId);
      li.Payments.push({
        PaymentTypeID: li._paymentType.PaymentTypeId,
        Amount: li.PaymentAmountToApply,
        ProfileID: payerProfileId,
        InvoiceNumber: invoiceNum,
        InvoiceLineItemID: li.LineItemId,
        CC_CreditCardNum: header?.CC_CreditCardNum,
        CC_ValidThrough: header?.CC_ValidThrough,
        CC_NameOnCard: header?.CC_NameOnCard,
        CC_BillingAddress1: header?.CC_BillingAddress1,
        CC_BillingAddress2: header?.CC_BillingAddress2,
        CC_BillingCity: header?.CC_BillingCity,
        CC_BillingState: header?.CC_BillingState,
        CC_BillingZip: header?.CC_BillingZip,
        CC_BillingZipExt: header?.CC_BillingZipExt,
        CC_PIN: header?.CC_PIN,
        CC_PhoneNum: header?.CC_PhoneNum,
        Dirty: true,
      } as InvoicePaymentUpsertDto);
    });
  }

  /**
   * Builds synthetic InvoicePaymentHeaderUpsertResultDto entries for gateway charges that failed before
   * upsertInvoice was ever called (i.e. the CC charge itself failed, not the backend-side persistence of an
   * already-successful charge). These are merged into result.PaymentHeaderResults so the existing warn-toast
   * handling in saveInvoice() - which only reads result.PaymentHeaderResults - surfaces them without needing
   * any changes to that handling. Indices continue on from the headers actually submitted to the backend so
   * they don't collide with real per-AP result indices.
   */
  private buildFailedAdvancedPaymentHeaderResults(
    failedResults: AdvancedCreditCardPaymentResult[]
  ): InvoicePaymentHeaderUpsertResultDto[] {
    const results: InvoicePaymentHeaderUpsertResultDto[] = [];
    let index = this.invoice.PaymentHeaders?.length ?? 0;

    for (const r of failedResults) {
      const detail = r.TransactionResult.RawTransactionResult || 'An error occurred processing this payment.';
      for (const header of r.PaymentInfoPaymentHeaderMap.PaymentHeaders) {
        results.push({
          Index: index++,
          PaymentTypeID: header.PaymentTypeID,
          PaymentHeaderID: 0,
          Success: false,
          Messages: [detail],
        });
      }
    }

    return results;
  }

  private checkForImbalance(): InvoiceImbalance {
    if (this.invoice.LineItemAdjustmentHeader.AdjustmentAmount < 0) {
      const adjustedLineItems = this.invoice.LineItems.filter((x) => x.AdjustmentAmountToApply < 0);
      for (const adjustedLineItem of adjustedLineItems) {
        if (getSafe(() => adjustedLineItem.Payments.length, 0) > 0 && !this.invoice.PaymentAdjustmentHeader.AdjustmentAmount) {
          return InvoiceImbalance.PaymentAppliedWithoutAnAdjustment;
        }
      }
    } else if (this.invoice.PaymentAdjustmentHeader.AdjustmentAmount < 0 && !this.invoice.LineItemAdjustmentHeader.AdjustmentAmount) {
      return InvoiceImbalance.OutstandingLineItemWithoutAnAdjustment;
    }
    return InvoiceImbalance.None;
  }

  // Seems hacky, but PrimeNG ConfirmDialog currently doesn't have yes/no/cancel so this handles yes & no, with the confirmRefund
  // method taking the cancel directly.
  public confirmAccept(shouldRefund: boolean) {
    this.shouldRefund = shouldRefund;
    this.confirmDialog.onAccept();
  }

  private confirmRefund(): Promise<boolean> {
    if (
      this.invoice.LineItems.some(
        (li) => !li.DeleteLineItem && li.Payments.some((p) => !p.DeletePayment && p.AdjustmentAmountToApply < 0 && !!p.CC_CreditCardNum)
      )
    ) {
      return new Promise((resolve, reject) => {
        this._confirmationService.confirm({
          message: this._translationService.getInstant('invoices.invoice-edit.refund-confirmation.message'),
          accept: () => {
            if (this.shouldRefund) {
              resolve(true);
            } else {
              resolve(false);
            }
          },
          reject: () => {
            reject();
            this._confirmationService.close();
          },
        });
      });
    } else {
      return new Promise((resolve, reject) => {
        resolve(false);
      });
    }
  }

  changeProfileClick() {
    this.showChangeProfile = true;
  }

  cancelChangeProfile() {
    this.showChangeProfile = false;
  }

  changeProfile(profileId: number) {
    if (profileId) {
      this.isLoading = true;
      this.showChangeProfile = false;

      this.profileId = profileId;
      this.invoice.InvoiceHeader.ProfileId = profileId;
      this.invoice.LineItems.forEach((li) => {
        li.Adjustments.forEach((adj) => {
          adj.ProfileId = profileId;
        });

        li.Payments.forEach((payment) => {
          payment.ProfileID = profileId;

          payment.Adjustments.forEach((payAdj) => {
            payAdj.ProfileId = profileId;
          });
        });
      });
      this.invoice.PaymentHeader.ProfileID = profileId;

      this._profileService.getProfile(this.profileId).then((data: ProfileRead) => {
        this.profile = data;
        this.isLoading = false;
      });

      this.invoiceBuilder.invoiceDetails.invoiceProfileChanged();
    }
  }

  onAddingNewPaymentChange(value: boolean) {
    this.addingNewPayment = value;
  }
}
